/* eslint-disable global-require */
import { useState, useEffect } from 'react';

import { parseCookiesToUser } from '@quintoandar/wat-cookie';

import { LISTING_BUSINESS_CONTEXT_FEATURE_FLAG } from 'containers/Agenda/constants';
import { getAgentIdByCookie } from 'containers/App/User/Data/useAgentData';
import { FEATURE_FLAGS } from 'enums/featureFlags';

const cacheTimeToLiveSeconds = 60;
const defaultFeatureFlagValue = false;
let provider;

const isServer = () => typeof window === 'undefined';

const localFeatureFlagValues = Object.freeze({
  AGENTS_EARLY_DEMAND: true,
  COMPLETE_INFO_LISTING_FEATURE_FLAG: true,
  DIRECT_REFERRAL: true,
  ENABLE_CONTACT_SUBMISSIONS: true,
  HOME_OWNER_INSURANCE: true,
  INTERCOM: true,
  LR_CR_CHAT: true,
  MIGRATION_TO_API_V3: true,
  OFFER_STATUS_LIST: true,
  PP_CR_CHAT: true,
  REGISTER_VISIT_V3: true,
  RESERVATION_ALLOWED: true,
  SELF_SERVICE_TAXES: true,
  TENANT_SERVICE_FEE: true,
  TRAZ_QUEM_COMPRA: true,
  VISIT_CONFIRMATION_BY_AGENT: true,
  VISIT_DONE_V3: true,
  VISIT_HOUSE_OWNER_CHAT_UNAVAILABLE_FEATURE_FLAG: false,
  agents_api_retry: true,
  agents_early_demand: true,
  create_visit_flag: true,
  disable_crm_offer_filters: false,
  enable_agent_activity: true,
  enable_offers_page_v2: true,
  enable_owner_contact_for_rent: true,
  enablePortfolioLogin: false,
  entry_access_routes_agents_api_enabled: true,
  fetch_from_client_portfolio_enabled: true,
  find_webapp_login_redirect_enabled: false,
  incognia_integration_enabled: false,
  isReviewAdCtaEnabled: true,
  is_extra_visits_shortcut_agenda_entrypoint_rent_enabled: true,
  is_extra_visits_shortcut_agenda_entrypoint_sale_enabled: true,
  is_extra_visits_shortcut_enabled: true,
  is_visit_cancellation_agent_enabled: true,
  keys_with_agent_key_confirmation_enabled: true,
  keys_with_agent_property_indication_enabled: true,
  listing_business_context: true,
  lr_cr_chat_v2: true,
  new_menu_home_item: true,
  new_menu_layout: true,
  non_pfa_clients_flag: true,
  offer_status_list: true,
  owner_information_enabled_for_3p_agent: true,
  redirect_to_identity: false,
  retry_max_retries: 3,
  retry_timeout_ms: 500,
  unpublish_listing_1p: true,
  visit_automatic_cancellation: true,
  visit_detail_status_component_v2: true,
  visit_details_price_table_rental_guarentee_enabled: true,
  visit_details_price_table_rental_guarantee_enabled: true,
  visit_finalization_dialog: true,
  visit_status_unavailable_hours: true,
});

const getLocalFeatureFlagValue = (key, defaultValue) =>
  Object.prototype.hasOwnProperty.call(localFeatureFlagValues, key)
    ? localFeatureFlagValues[key]
    : defaultValue;

if (isServer()) {
  const { createClientWithLazyLoad } = require('configcat-js-ssr');

  provider = process.env.CONFIG_CAT_KEY
    ? createClientWithLazyLoad(process.env.CONFIG_CAT_KEY, {
        cacheTimeToLiveSeconds,
      })
    : {
        getValueAsync: (key, defaultValue = defaultFeatureFlagValue) =>
          Promise.resolve(getLocalFeatureFlagValue(key, defaultValue)),
      };
} else {
  const {
    getLazyLoadingProvider,
    LazyLoadMode,
  } = require('@quintoandar/feature-flag-tools');

  provider = process.env.CONFIG_CAT_KEY
    ? getLazyLoadingProvider(process.env.CONFIG_CAT_KEY, LazyLoadMode.LIGHT)
    : {
        isFeatureEnabled: (key, defaultValue = defaultFeatureFlagValue) =>
          Promise.resolve(getLocalFeatureFlagValue(key, defaultValue)),
      };
}

export const getRelistingEnabledFlag = () =>
  isServer()
    ? provider?.getValueAsync(
        LISTING_BUSINESS_CONTEXT_FEATURE_FLAG,
        defaultFeatureFlagValue,
      )
    : provider?.isFeatureEnabled(
        LISTING_BUSINESS_CONTEXT_FEATURE_FLAG,
        defaultFeatureFlagValue,
      );

export const getVisitAutomaticCancellationFeatureFlag = () => {
  const agentId = getAgentIdByCookie();

  if (isServer()) {
    return provider?.getValueAsync(
      FEATURE_FLAGS.VISIT_AUTOMATIC_CANCELLATION,
      defaultFeatureFlagValue,
    );
  }

  return provider?.isFeatureEnabled(
    FEATURE_FLAGS.VISIT_AUTOMATIC_CANCELLATION,
    defaultFeatureFlagValue,
    { identifier: String(agentId) },
  );
};

export const getFeatureFlagEnabled = (key) => {
  const userFromCookie = parseCookiesToUser();

  // eslint-disable-next-line camelcase
  const agentId = userFromCookie?.agent_id || '';

  if (isServer()) {
    return provider?.getValueAsync(key, defaultFeatureFlagValue);
  }

  return provider?.isFeatureEnabled(key, defaultFeatureFlagValue, {
    identifier: String(agentId),
  });
};

export const useFeatureFlag = (key, options, defaultValue = false) => {
  const [isEnabled, setIsEnabled] = useState(defaultValue);

  if (isServer()) {
    return isEnabled;
  }

  const getFeatureFlag = async () => {
    const isFeatureEnabled = await provider?.isFeatureEnabled(
      key,
      defaultValue,
      { ...options },
    );
    setIsEnabled(isFeatureEnabled);
  };

  useEffect(() => {
    getFeatureFlag();
  }, [key, defaultValue, options]);

  return isEnabled;
};
